import { IconRadioChecked } from '@components/icons/radio-checked';
import { RadioGroup } from '@components/radio-group';
import { Select } from '@components/select';
import { TextField } from '@components/text-field';
import { getScopedTranslate } from '@locale/translate';
import { asteriskLabel } from '@utils/strings';

import { FieldDropzone } from './field-dropzone';
import { useOptionsFieldActions } from './use-field-actions';
import { useFieldOptions } from './use-field-options';

import { ValueMenu, conditionsTab } from '../conditions-tab';
import { useFormBuilderContext } from '../context';
import { FieldEdit } from '../field-edit';
import { FieldConfig } from '../field-registry';
import { FieldSidebarLabel } from '../fields-sidebar';
import { optionsTab } from '../options-tab';
import { RequiredSwitch } from '../required-switch';

type Config = FieldConfig<'input-single-choice'>;

const Display: Config['Display'] = ({ field }) => {
  const options = useFieldOptions(field);
  return (
    <RadioGroup
      label={asteriskLabel(field.label, field.isRequired)}
      defaultValue={field.defaultValue ?? undefined}
    >
      {options.map((option) => (
        <RadioGroup.Option key={option.value} label={option.label} value={option.value} />
      ))}
    </RadioGroup>
  );
};

const FormBuilderSingleChoice: Config['Component'] = ({ field, dragHandle }) => {
  const actions = useFormBuilderContext().store.actions;
  const menuActions = useOptionsFieldActions(field);
  const options = useFieldOptions(field);

  return (
    <FieldDropzone field={field}>
      <FieldEdit
        field={field}
        dragHandle={dragHandle}
        menu={menuActions}
        display={<Display field={field} />}
        edit={
          <>
            <TextField
              onChange={actions.createChangeHandler(field, 'label')}
              defaultValue={field.label}
              label={t('label')}
              maxChars={255}
            />
            <Select
              defaultValue={field.defaultValue ?? undefined}
              onChange={actions.createChangeHandler(field, 'defaultValue')}
              label={t('default')}
              options={options}
            />
            <RequiredSwitch field={field} />
          </>
        }
      />
    </FieldDropzone>
  );
};

const { t } = getScopedTranslate({
  en: {
    title: 'Single choice',
    label: 'Label',
    default: 'Default option',
  },
});

export const singleChoiceConfig: Config = {
  kind: 'input-single-choice',
  title: t('title'),
  group: 'selections',
  propertiesTabs: [optionsTab, conditionsTab],
  Display,
  TreeDisplay: ({ field }) => <FieldSidebarLabel label={field.label} />,
  Icon: IconRadioChecked,
  Component: FormBuilderSingleChoice,
  condition: {
    comparators: ['is', 'not_is'],
    ValueMenu: ({ field, ...rest }) => {
      const options = useFieldOptions(field);
      return <ValueMenu options={options} {...rest} />;
    },
  },
};
